// Cities and World Heritage sites at a date.
//
//   cities   every city in cities.json (the file lists urban centres that exist today), shown as
//            it was on the date:
//              population  the latest series year ≤ year(D); none (pop 0, pop_year 0) before the
//                          first datum, never a later count; an undated city keeps its one value
//              capital     the flag, kept only inside its dated term (capital_from ≤ D < capital_to),
//                          and only while its state holds land (a fact may also grant or remove it)
//              country     the state that holds most of the city's own state's land, while that holds none
//              name        the name in force on D, when a rename is curated
//              figure      no population at all where a fact withholds it (a delineation artefact)
//   wonders  sites whose UNESCO inscription year ≤ year(D)
//
// The dated facts are curated (world-state `cities`, each with sources) and joined on by iso3 and
// name (`withFacts`). `cityTimeline` caches the result per year and per stretch of dates between two
// fact dates, so a date returns the same array as its neighbour unless a fact turns over.

import type { City, DateStr, Wonder } from '../core/schema.ts';
import { inSpan, yearOf } from '../core/dates.ts';
import type { CityFact, CityName } from '../data/index.ts';
import type { Land, LandOn } from './land.ts';
import { byDate, latestAt, uniq } from './util.ts';

/** No population figure at the date (a city before its first datum). */
const NO_POPULATION = 0;
/** `pop_year` of a figure with no year. */
const UNDATED = 0;

/** The memo key of a timeline with no land: one border picture for every date. */
const NO_LAND: Land = { key: {}, lost: () => false, heir: () => null };

/** A city with its curated facts joined on. */
export interface DatedCity extends City {
  capital_from?: DateStr;
  capital_to?: DateStr;
  names?: CityName[];
  centre_names?: string[];
}

/** The date-independent cities: facts joined on, sources merged. Cities with no fact are the same objects. */
export function withFacts(cities: City[], facts: readonly CityFact[]): DatedCity[] {
  if (facts.length === 0) {
    return cities;
  }

  const byCity = new Map(facts.map((f) => [`${f.iso3}|${f.city}`, f]));
  return cities.map((c) => {
    const fact = byCity.get(`${c.iso3}|${c.name}`);
    if (!fact) {
      return c;
    }

    const { iso3, city, note, sources, figure, ...dated } = fact;
    const joined = { ...c, ...dated, sources: uniq([...c.sources, ...sources]) };
    return figure === 'withheld' ? withoutFigure(joined) : joined;
  });
}

/** The city with no population and no series: citiesAt leaves a city with no series as it is. */
function withoutFigure(c: DatedCity): DatedCity {
  const { series, ...rest } = c;
  return { ...rest, pop: NO_POPULATION, pop_year: UNDATED };
}

/** Population by the series year ≤ `year`; no figure (and no year) before the first datum. */
export function citiesAt(cities: DatedCity[], year: number): DatedCity[] {
  return cities.map((c) => {
    if (!c.series || c.series.length === 0) {
      return c;
    }

    const [popYear, pop] = latestAt(c.series, year) ?? [UNDATED, NO_POPULATION];
    return popYear === c.pop_year && pop === c.pop ? c : { ...c, pop, pop_year: popYear };
  });
}

function inTerm(c: DatedCity, date: DateStr): boolean {
  return (c.capital_from === undefined || c.capital_from <= date) && (c.capital_to === undefined || date < c.capital_to);
}

/** The city as its dated facts make it on `date`: capital flag inside its term, the name in force, the state that holds it. */
function onDate(c: DatedCity, date: DateStr, land: Land): DatedCity {
  const lost = land.lost(c.iso3);
  const capital = c.capital && inTerm(c, date) && !lost;
  const named = c.names?.find((n) => inSpan(n, date));
  const holder = lost ? land.heir(c.iso3) : null;
  if (!named && capital === c.capital && holder === null) {
    return c;
  }

  const rename = named ? { name: named.name, sources: uniq([...c.sources, ...named.sources]) } : {};
  const state = holder === null ? {} : { iso3: holder };
  return { ...c, capital, ...rename, ...state };
}

/** Every date on which some city's capital term or name changes. */
function turnovers(cities: DatedCity[]): DateStr[] {
  const dates = new Set<DateStr>();
  for (const c of cities) {
    for (const d of [c.capital_from, c.capital_to, ...(c.names ?? []).flatMap((n) => [n.from, n.to])]) {
      if (d !== undefined && d !== null) {
        dates.add(d);
      }
    }
  }
  return [...dates].sort(byDate);
}

export type CitiesOn = (date: DateStr) => DatedCity[];

/** Cities on a date (see the header); the same array for every date of a year between two turnovers and within one border picture. */
export function cityTimeline(cities: DatedCity[], landOn?: LandOn): CitiesOn {
  const turns = turnovers(cities);
  const byYear = new Map<number, DatedCity[]>();
  const memo = new WeakMap<object, Map<string, DatedCity[]>>();

  return (date) => {
    const land = landOn?.(date) ?? NO_LAND;
    const stretches = memo.get(land.key) ?? new Map<string, DatedCity[]>();
    memo.set(land.key, stretches);

    const year = yearOf(date);
    const stretch = `${year}|${turns.filter((t) => t <= date).length}`;
    const hit = stretches.get(stretch);
    if (hit) {
      return hit;
    }

    const ofYear = byYear.get(year) ?? citiesAt(cities, year);
    byYear.set(year, ofYear);

    const made = ofYear.map((c) => onDate(c, date, land));
    stretches.set(stretch, made);
    return made;
  };
}

export function wondersAt(wonders: Wonder[], year: number): Wonder[] {
  return wonders.filter((w) => w.year <= year);
}
